import { Component, HostBinding, OnDestroy, OnInit, signal, inject, ChangeDetectionStrategy } from '@angular/core';
import {AuthService} from "../auth/auth.service";
import { Title} from "@angular/platform-browser";
import {NotificationService} from "./notifications/services/notification.service";
import {Subscription} from "rxjs";
import {MatDialog} from "@angular/material/dialog";
import {AskPermissionsComponent} from "./ask-permissions/ask-permissions.component";
import {TranslateService} from "@ngx-translate/core";
import {languages} from "../language-manager/language";

import type User from "../auth/models/user.modes";
import type {SideBarMode} from "./navigation/navigation-constants";
import {loadScript} from "../utilities/util";
import {cookieConsentKey} from "./admin/toggles/storageKeys";
import {CookieConsentComponent} from "./cookie-consent/cookie-consent.component";
import {environment} from "../../environments/environment";

let zendeskRanOnce = false;

@Component({
    selector: 'app-main',
    templateUrl: './main.component.html',
    styleUrls: ['./main.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MainComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private title = inject(Title);
  private notificationService = inject(NotificationService);
  private translateService = inject(TranslateService);
  private dialog = inject(MatDialog);


  private readonly storageAskedPrem = 'askedPermission';
  readonly languages = languages;

  toggleMenu = signal(false);

  user: User;
  profilePic: string;

  sideBarMode: SideBarMode;

  subs: Subscription = new Subscription();
  askedPermission: any;

  @HostBinding('class.right-panel-open') notifBarOpened = false;
  @HostBinding('class.folded-state') isFolded = false;

  ngOnInit(): void {
    const isCargoCube = window.location.href.includes('cargo-cube');
    this.title.setTitle('Global Control Tower');
    this.askedPermission = localStorage.getItem(this.storageAskedPrem);
    if(this.askedPermission) this.askedPermission = JSON.parse(this.askedPermission);
    const cookieStorageItem = localStorage.getItem(cookieConsentKey);
    let cookiesFromDialog;
    if(!cookieStorageItem) {
      this.dialog.open(CookieConsentComponent).afterClosed().subscribe(res => {
        cookiesFromDialog = res ? 'true' : 'false';
        localStorage.setItem(cookieConsentKey, res ? 'true' : 'false');
        window?.zE("messenger:set", "cookies", !!res)
      })
    }

    if(this.askedPermission?.location)
      navigator.geolocation.getCurrentPosition((location) => window.loc = location);

    //if(environment.type != 'dev')
      this.askedPermissionDialog();
    this.user = this.authService.getLoggedInUser();

    this.fixUserData();

    this.subscribeNotificationNavigation();

    if(!isCargoCube && environment.production) {
      loadScript('https://static.zdassets.com/ekr/snippet.js?key=c2df32d5-9038-4498-88c1-abf622c6397c', true, true, () => {
        const currentLang = this.languages.find(e => e.code === this.translateService.currentLang);
        window['currentFn'] = () => {
          const cLang = this.languages.find(e => e.code === this.translateService.currentLang);
          window.zE("messenger:set", "locale", cLang.code === 'en' ? 'en-US' : cLang.code);
        }
        if (!zendeskRanOnce) {
          window.zE('messenger:on', 'open', () => {
            window['currentFn']();
          });
          zendeskRanOnce = true;
        }

        window.zESettings = {
          webWidget: {
            contactOptions: {
              enabled: true,
              contactButton: { '*': 'Get in touch' }
            },
            position: {horizontal: 'right', vertical: 'bottom'},
            offset: {vertical: '60px'},
            zIndex: 200,
            color: {
              //theme: '#ff0007',
              //launcher: '#ff0007',
              //launcherText: '#ff0007',
              button: '#4b6a8f',
              //resultLists: '#ff0007',
              //header: '#ff0007',
              //articleLinks: '#ff0007'
            },
            contactForm: {
              fields: [
                { id: 'description', prefill: { '*': 'Tell us what we can help you with?' } },
                { id: 'email', prefill: { '*': this.user.Email } },
                { id: 'name', prefill: { '*': this.user.FirstName + ' ' + this.user.LastName } }
              ]
            }
          }
        };
        window.zE("messenger:set", "locale", currentLang.code === 'en' ? 'en-US' : currentLang.code);
        if(cookieStorageItem === "true" || cookieStorageItem === "false" && !cookiesFromDialog)
          window.zE("messenger:set", "cookies", cookieStorageItem === "true")
        window.zE('messenger', 'show');
        window.zE("messenger", "loginUser", (callback) => {
          this.authService.getJwtTokenZendesk().subscribe(res => {
            callback(res);
          });
        });
      }, null, 'ze-snippet');
    }

  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    try {
      window.zE?.('messenger', 'hide');
    } catch (e) {

    }
  }

  askedPermissionDialog() {
    if(!this.askedPermission)
      this.dialog.open(AskPermissionsComponent, {
        panelClass: ['no-padding-dialog'],
        autoFocus: false,
        disableClose: true
      }).afterClosed().subscribe(val => {
        let objectToSave = {} as any;

        if(!val) {
          objectToSave.location = false;
        } else {
          if(val.location) {
            navigator.geolocation.getCurrentPosition((location) => {
              window.loc = location;
              objectToSave.location = true;
            }, () => objectToSave.location = false);
          }
        }

        this.savePremObj(objectToSave)
      });
  }

  savePremObj(val) {
    localStorage.setItem(this.storageAskedPrem, JSON.stringify(val));
  }

  subscribeNotificationNavigation() {
    this.subs.add(this.notificationService.closeOnNavigate.subscribe(() => this.toggle()));
  }

  fixUserData() {
    if(!this.user.Email?.trim())
      this.user.Email = "No Mail";
    if(!this.user.profilePic)
      this.profilePic = '../assets/user-photo.png';
  }

  modeChange(mode: SideBarMode) {
    this.sideBarMode = mode;
    if (mode === 'closed')
      this.isFolded = true;
    else if (mode === 'opened')
      this.isFolded = false;

  }

  toggle() {
    this.notifBarOpened = !this.notifBarOpened;
    this.notificationService.panelOpened.next(this.notifBarOpened);
  }

}
